import { createTheme } from '@mui/material/styles';

/**
 * SMS.by style — по образцу https://sms.by
 * Тёмный хедер (#303030), градиент жёлтый→фиолетовый, акцент purple/pink
 */
export const smsByTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#FFB300',
      light: '#ffc233',
      dark: '#e6a100',
      /** Белый текст на жёлтом — как в макете Figma (SMS светлая) */
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#8b5cf6',
      light: '#a78bfa',
      dark: '#6d28d9',
      contrastText: '#fff',
    },
    background: {
      default: '#fcfcfc',
      paper: '#ffffff',
    },
    text: {
      primary: '#0d0d0d',
      secondary: '#94a3b8',
    },
  },
  typography: {
    fontFamily: '"Montserrat", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#303030',
          color: '#fff',
          boxShadow: 'none',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0px 1px 1px 0px rgba(0,0,0,0.14)',
          borderRadius: 8,
        },
      },
    },
    /** Вкладки «Мои боты / Сценарии / Шаблоны» — только тема smsby используется на этой странице */
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 72,
          '& .MuiTabs-indicator': {
            height: 2,
            backgroundColor: '#FFB300',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 72,
          textTransform: 'uppercase',
          fontSize: 14,
          fontWeight: 600,
          color: '#94a3b8',
          '&.Mui-selected': {
            color: '#FFB300',
          },
        },
      },
    },
    /** Единый стиль кнопок SMS: pill 44px, высота 37px, #FFB300 (как в макете) */
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: '44px',
          minHeight: 37,
          paddingLeft: '10px',
          paddingRight: '10px',
          gap: '10px',
          fontSize: 14,
          fontWeight: 500,
          lineHeight: 1.2,
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
          '& .MuiButton-startIcon': { marginLeft: 0, marginRight: 0 },
          '& .MuiButton-endIcon': { marginLeft: 0, marginRight: 0 },
        },
        containedPrimary: {
          backgroundColor: '#FFB300',
          color: '#ffffff',
          border: '1px solid #FFB300',
          '&:hover': {
            backgroundColor: '#e6a200',
            borderColor: '#e6a200',
            color: '#ffffff',
          },
        },
        containedSecondary: {
          backgroundColor: '#8b5cf6',
          color: '#fff',
          border: '1px solid #8b5cf6',
          '&:hover': {
            backgroundColor: '#7c3aed',
            borderColor: '#7c3aed',
          },
        },
        containedError: {
          border: '1px solid',
          borderColor: 'transparent',
        },
        outlined: {
          borderWidth: 1,
          borderColor: '#FFB300',
          color: 'rgba(0, 0, 0, 0.87)',
          '&:hover': {
            borderColor: '#e6a200',
            backgroundColor: 'rgba(255, 179, 0, 0.12)',
          },
        },
        text: {
          color: 'rgba(0, 0, 0, 0.87)',
          minHeight: 37,
          '&:hover': {
            backgroundColor: 'rgba(255, 179, 0, 0.14)',
          },
        },
        sizeSmall: {
          minHeight: 37,
          paddingLeft: '10px',
          paddingRight: '10px',
        },
        sizeMedium: {
          minHeight: 37,
          paddingLeft: '10px',
          paddingRight: '10px',
        },
        sizeLarge: {
          minHeight: 40,
          paddingLeft: '12px',
          paddingRight: '12px',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: '44px',
          padding: 8,
          '&:hover': {
            backgroundColor: 'rgba(255, 179, 0, 0.16)',
          },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: '44px',
          minHeight: 37,
          px: '10px',
          textTransform: 'none',
          fontWeight: 500,
          borderColor: '#FFB300',
          color: 'rgba(0, 0, 0, 0.87)',
          '&.Mui-selected': {
            backgroundColor: 'rgba(255, 179, 0, 0.35)',
            color: 'rgba(0, 0, 0, 0.87)',
            '&:hover': {
              backgroundColor: 'rgba(255, 179, 0, 0.45)',
            },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#FFB300',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#FFB300',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            color: '#FFB300',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#FFB300',
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
        },
      },
    },
  },
});

/**
 * DataEdge style — тёмная тема по образцу https://dataedge.md
 * Тёмный фон, яркий синий акцент (#00aaff), золотая обводка кнопок
 */
export const dataEdgeTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#00aaff',
      light: '#38bdf8',
      dark: '#0284c7',
      contrastText: '#fff',
    },
    secondary: {
      main: '#fbbf24',
      light: '#fcd34d',
      dark: '#f59e0b',
      contrastText: '#0f0f0f',
    },
    background: {
      default: '#0f0f0f',
      paper: '#1a1a1a',
    },
    text: {
      primary: '#ffffff',
      secondary: '#94a3b8',
    },
  },
  typography: {
    fontFamily: '"Montserrat", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#1a1a1a',
          borderBottom: '1px solid rgba(0, 170, 255, 0.2)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(26, 26, 26, 0.9)',
          border: '1px solid rgba(0, 170, 255, 0.15)',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: '44px',
          minHeight: 37,
          paddingLeft: '10px',
          paddingRight: '10px',
          gap: '10px',
          fontSize: 14,
          fontWeight: 500,
          lineHeight: 1.2,
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
          '& .MuiButton-startIcon': { marginLeft: 0, marginRight: 0 },
          '& .MuiButton-endIcon': { marginLeft: 0, marginRight: 0 },
        },
        containedPrimary: {
          backgroundColor: '#00aaff',
          color: '#0f0f0f',
          border: '1px solid #00aaff',
          '&:hover': {
            backgroundColor: '#38bdf8',
            borderColor: '#38bdf8',
          },
        },
        containedSecondary: {
          backgroundColor: '#fbbf24',
          color: '#0f0f0f',
          border: '1px solid #fbbf24',
          '&:hover': {
            backgroundColor: '#fcd34d',
            borderColor: '#fcd34d',
          },
        },
        containedError: {
          border: '1px solid',
          borderColor: 'transparent',
        },
        outlined: {
          borderWidth: 1,
          borderRadius: '44px',
          borderColor: '#fbbf24',
          color: '#fbbf24',
          '&:hover': {
            borderColor: '#fcd34d',
            backgroundColor: 'rgba(251, 191, 36, 0.08)',
          },
        },
        text: {
          minHeight: 37,
          '&:hover': {
            backgroundColor: 'rgba(0, 170, 255, 0.12)',
          },
        },
        sizeSmall: {
          minHeight: 37,
          paddingLeft: '10px',
          paddingRight: '10px',
        },
        sizeMedium: {
          minHeight: 37,
          paddingLeft: '10px',
          paddingRight: '10px',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: '44px',
          '&:hover': {
            backgroundColor: 'rgba(0, 170, 255, 0.12)',
          },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: '44px',
          minHeight: 37,
          px: '10px',
          textTransform: 'none',
          fontWeight: 500,
          color: '#fbbf24',
          borderColor: 'rgba(251, 191, 36, 0.45)',
          '&.Mui-selected': {
            backgroundColor: 'rgba(0, 170, 255, 0.28)',
            color: '#fff',
            borderColor: '#00aaff',
            '&:hover': {
              backgroundColor: 'rgba(0, 170, 255, 0.38)',
            },
          },
        },
      },
    },
  },
});

export type ThemeId = 'smsby' | 'dataedge';

export const getTheme = (id: ThemeId) => (id === 'smsby' ? smsByTheme : dataEdgeTheme);
